import {
	Menubar,
	MenubarContent,
	MenubarItem,
	MenubarMenu,
	MenubarSub,
	MenubarSubContent,
	MenubarSubTrigger,
	MenubarTrigger,
} from "@radix-ui/react-menubar";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Button } from "@/components/ui/button";
import type { MonitorState } from "@/components/widgets/Monitor/type";
import type { WidgetScreenerState } from "@/components/widgets/Screener/type";
import { useTabs } from "@/context/tabs/TabsContext";
import { useEventSubscription } from "@/hooks/useEventSubscription";
import { EVENTS } from "@/shared/eventEmiters/consts";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import type { ViewId } from "@/shared/types/common";
import { SettingsOptions } from "@/shared/types/common";
import sprites from "/src/shared/assets/sprite.svg";
import {
	DEFAULT_PANEL_MODE,
	menuItems,
	type PanelMode,
} from "./MosaicMenuItems";

const DEFAULT_COLOR = "inherit";
const TILES_WITHOUT_DEFAULT_LINKING: ViewId[] = [];
const ALLOWED_SETTINGS: Record<ViewId, SettingsOptions[]> = {
	Screener: [
		SettingsOptions.CLICK_TRIGGER,
		SettingsOptions.PANEL_MODE,
		SettingsOptions.DOWNLOAD_CSV,
	],
	Monitor: [
		SettingsOptions.CLICK_TRIGGER,
		SettingsOptions.PANEL_MODE,
		SettingsOptions.DOWNLOAD_CSV,
	],
	News: [SettingsOptions.FILTERING],
	Chart: [],
	"Drift Forecast": [],
	Elements: [],
	"Earnings Analysis": [],
	Backtest: [
		SettingsOptions.FILTERING,
		SettingsOptions.CLICK_TRIGGER,
		SettingsOptions.DENSE_MODE,
		SettingsOptions.MASTER_DETAIL,
		SettingsOptions.GROUPING,
		SettingsOptions.CHART_ON_HOVER,
	],
	Holders: [],
	Chat: [],
	Financials: [],
	Navigation: [],
	Markets: [],
	"Advanced Quote": [],
	Quote: [],
	"Dark Short": [],
	Holdings: [],
	Insiders: [],
};

interface MosaicMenuProps {
	onColorChange: (color: string | null) => void;
	baseId: ViewId;
	tileId: string;
	passedColor?: string | null;
	eventEmitter: EventEmitter;
}

export const MosaicMenu = ({
	onColorChange,
	baseId,
	tileId,
	eventEmitter,
	passedColor,
}: MosaicMenuProps) => {
	const { getWidgetState, setWidgetState } = useTabs();
	const widgetState = getWidgetState(tileId as ViewId);
	const isOneClickTrigger = (widgetState as MonitorState | WidgetScreenerState)
		?.isOneClickTrigger;
	const panelMode = (widgetState as MonitorState | WidgetScreenerState)
		?.panelMode;

	const isExcludedBaseId = TILES_WITHOUT_DEFAULT_LINKING.includes(baseId);

	const defaultColor =
		passedColor ?? (menuItems[0]?.subItems?.[0]?.color || null);

	const defaultSelectedColor = passedColor
		? passedColor
		: isExcludedBaseId
			? null
			: defaultColor;

	const [selectedColor, setSelectedColor] = useState<string | null>(
		defaultSelectedColor,
	);

	const [isWindowExpanded, setIsWindowExpanded] = useState<boolean>(true);

	const handleColorChange = (color: string | null) => {
		setSelectedColor(color);
		onColorChange(color);
	};

	const handleMenubarItemClick = (itemId: SettingsOptions) => {
		switch (itemId) {
			case SettingsOptions.FILTERING: {
				const newExpandedState = !isWindowExpanded;
				setIsWindowExpanded(newExpandedState);
				eventEmitter.emit(EVENTS.EXPANDED_WINDOW, newExpandedState);
				break;
			}

			case SettingsOptions.CLICK_TRIGGER:
				setWidgetState(tileId as ViewId, {
					...widgetState,
					isOneClickTrigger: !isOneClickTrigger,
				});
				break;

			case SettingsOptions.DOWNLOAD_CSV:
				eventEmitter.emit(EVENTS.DOWNLOAD_CSV, true);
				break;

			default:
				return;
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: correct deps array
	useEffect(() => {
		if (!isExcludedBaseId) {
			setSelectedColor(defaultColor);
			onColorChange(defaultColor);
		}
	}, [defaultColor, isExcludedBaseId]);

	const getFilteredMenuItems = (baseId: ViewId) => {
		const allowedOptions = ALLOWED_SETTINGS[baseId] || [];
		return menuItems.filter(
			(item) =>
				allowedOptions.includes(item.id as SettingsOptions) ||
				item.id === SettingsOptions.LINK,
		);
	};

	const filteredMenuItems = getFilteredMenuItems(baseId);

	useEventSubscription(
		eventEmitter,
		EVENTS.EXPANDED_WINDOW,
		setIsWindowExpanded,
	);

	return (
		<Menubar>
			<MenubarMenu>
				<MenubarTrigger asChild>
					<Button
						variant="outline"
						type="button"
						className="bg-transparent font-bold pt-1 px-1 rounded border-none h-7"
						style={{
							backgroundColor: selectedColor || DEFAULT_COLOR,
						}}
					>
						<svg
							aria-label="settings"
							role="img"
							fill="#fff"
							className="w-4 h-3"
						>
							<use xlinkHref={`${sprites}#settings`} />
						</svg>
					</Button>
				</MenubarTrigger>

				{ReactDOM.createPortal(
					<MenubarContent className="bg-popover p-1 rounded-lg shadow-[0_40px_70px_rgba(0,0,0,1)] z-50 text-[var(--light-gray)] w-max">
						{filteredMenuItems.map((item) =>
							item.subItems ? (
								<MenubarSub key={item.id}>
									<MenubarSubTrigger className="flex items-center px-3 text-sm hover:bg-(--dark-background) hover:text-gold rounded cursor-pointer focus:outline-hidden">
										{item.text}
										<svg
											aria-label="submenu"
											role="img"
											fill="#fff"
											className="ml-auto w-2 h-2"
										>
											<use xlinkHref={`${sprites}#chevron-right`} />
										</svg>
									</MenubarSubTrigger>

									{ReactDOM.createPortal(
										<MenubarSubContent className="mt-1 bg-popover text-golden p-1 rounded-lg shadow-[0_40px_70px_rgba(0,0,0,1)] z-50 text-primary w-max">
											{item.subItems.map((subItem) => (
												<MenubarItem
													key={subItem.id}
													className={`flex items-center py-1 px-3 text-sm hover:text-accent-foreground hover:bg-(--dark-background) rounded cursor-pointer focus:outline-hidden ${
														subItem.color === selectedColor
															? "text-gold"
															: "text-stone-400"
													}`}
													onClick={() =>
														subItem.color || subItem.color === null
															? handleColorChange(subItem.color ?? null)
															: setWidgetState(tileId as ViewId, {
																	...widgetState,
																	panelMode: subItem.id as PanelMode,
																})
													}
												>
													{subItem?.color && (
														<span
															className="w-3 h-3 mr-2"
															style={{ backgroundColor: subItem.color }}
														/>
													)}
													{subItem.text}
													{subItem?.color !== null &&
														!subItem?.color &&
														(panelMode === subItem.id ||
															(!panelMode &&
																DEFAULT_PANEL_MODE === subItem.id)) && (
															<svg
																aria-label="submenu"
																role="img"
																fill="#fff"
																className="ml-2 w-2 h-2"
															>
																<use xlinkHref={`${sprites}#info`} />
															</svg>
														)}
												</MenubarItem>
											))}
										</MenubarSubContent>,
										document.body,
									)}
								</MenubarSub>
							) : (
								<MenubarItem
									key={item.id}
									onClick={() => handleMenubarItemClick(item.id)}
									className="flex items-center py-1 px-3 text-sm hover:bg-(--dark-background) hover:text-gold rounded cursor-pointer focus:outline-hidden"
								>
									{item.text}
									{item.id === SettingsOptions.CLICK_TRIGGER &&
										isOneClickTrigger && (
											<svg
												aria-label="submenu"
												role="img"
												fill="#fff"
												className="ml-2 w-2 h-2"
											>
												<use xlinkHref={`${sprites}#info`} />
											</svg>
										)}
								</MenubarItem>
							),
						)}
					</MenubarContent>,
					document.body,
				)}
			</MenubarMenu>
		</Menubar>
	);
};
